import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import usePopup from 'hooks/usePopup';
import Button from 'kingpin/atoms/Button';

import InlineDialog from '../../InlineDialog';
import Row from '../../Common/Row';
import { UserIconFromId } from '../../Common/UserIcon';
import Typography from 'kingpin/atoms/Typography';
import List from '../../List';
import useSearchUsers from './useSearchUsers';
import TextInput from '../../../kingpin/atoms/TextInput';

const UserItemDiv = styled.div<{ onClick?: () => void; isLoading: boolean }>`
  padding: 4px 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
  ${(props) => (props.onClick ? 'cursor: pointer;' : '')}

  ${(props) =>
    props.isLoading &&
    `
    cursor: not-allowed;
    opacity: 0.7;
  `}

  .showOnMouseOver {
    visibility: hidden;
  }

  &:hover {
    .clickable {
      ${(props) => (props.onClick ? 'opacity: 0.7;' : '')}
    }

    .showOnMouseOver {
      visibility: visible;
    }
  }
`;

const UserItem = ({
  selectedUsers,
  user,
  onUserAdded,
  isLoading,
}: {
  selectedUsers: (UserManagement.PendingUser | UserManagement.SignedUpUser)[];
  user: UserManagement.PendingUser | UserManagement.SignedUpUser;
  onUserAdded: (
    user: UserManagement.PendingUser | UserManagement.SignedUpUser,
  ) => Promise<void>;
  isLoading: boolean;
}) => {
  const [isSelected, setIsSelected] = useState<boolean>(
    selectedUsers.some((u) => u.id === user.id),
  );
  useEffect(() => {
    setIsSelected(selectedUsers.some((u) => u.id === user.id));
  }, [selectedUsers, user.id]);
  const onAddClicked = useCallback(() => {
    if (isSelected || isLoading) {
      return;
    }

    onUserAdded(user);
  }, [isLoading, isSelected, onUserAdded, user]);

  return (
    <UserItemDiv
      onClick={isSelected ? undefined : onAddClicked}
      isLoading={isLoading}
    >
      <Row centerAlign className="clickable">
        <UserIconFromId userId={user.id} />
        <Typography.Body type="Body 12">{user.displayName}</Typography.Body>
      </Row>
      {isSelected && (
        <Button
          label="Added"
          icon="checkmark"
          isDisabled
          type="Ghost"
          size="Small"
        />
      )}
      {!isSelected && (
        <div className="showOnMouseOver">
          <Button
            onClick={onAddClicked}
            isDisabled={isLoading}
            label={'Add'}
            icon={'add'}
            type="Primary"
            size="Small"
            testId={`add-user-${user.id}`}
          />
        </div>
      )}
    </UserItemDiv>
  );
};

const UserList = ({
  isLoading,
  users,
  selectedUsers,
  onUserAdded,
  searchDivRef,
}: {
  isLoading: boolean;
  users: (UserManagement.PendingUser | UserManagement.SignedUpUser)[];
  selectedUsers: (UserManagement.PendingUser | UserManagement.SignedUpUser)[];
  onUserAdded: (
    user: UserManagement.PendingUser | UserManagement.SignedUpUser,
  ) => Promise<void>;
  searchDivRef: React.MutableRefObject<HTMLDivElement | null>;
}) => {
  const searchDivWidth = useMemo(() => {
    if (!searchDivRef.current) {
      return undefined;
    }

    if (process.env.NODE_ENV === 'test') {
      return 400;
    }

    return searchDivRef.current.clientWidth;
  }, [searchDivRef]);

  return (
    <div style={{ width: searchDivWidth, maxHeight: '400px' }}>
      {users.map((u) => (
        <UserItem
          key={u.id}
          selectedUsers={selectedUsers}
          user={u}
          onUserAdded={onUserAdded}
          isLoading={isLoading}
        />
      ))}
    </div>
  );
};

const AddUsersSearch = ({
  selectedUsers,
  isLoading,
  onUserAdded,
  usersGreenList,
}: {
  selectedUsers: (UserManagement.PendingUser | UserManagement.SignedUpUser)[];
  isLoading: boolean;
  onUserAdded: (
    user: UserManagement.PendingUser | UserManagement.SignedUpUser,
  ) => Promise<void>;
  /**
   * Optionally restrict the list of users who can be added from this control.
   * By default, all users in the system will be shown
   */
  usersGreenList?: (UserManagement.PendingUser | UserManagement.SignedUpUser)[];
}) => {
  const { filteredUsers, searchText, onSearchTextChanged } =
    useSearchUsers(usersGreenList);
  const { isOpen, open, close } = usePopup();
  const searchDivRef = useRef<HTMLDivElement | null>(null);

  return (
    <>
      <InlineDialog
        isOpen={isOpen}
        onClose={close}
        placement={'bottom-start'}
        content={
          <List
            noMargin
            noBorder
            Body={
              <UserList
                isLoading={isLoading}
                users={filteredUsers}
                onUserAdded={onUserAdded}
                selectedUsers={selectedUsers}
                searchDivRef={searchDivRef}
              />
            }
            Headers={null}
          />
        }
      >
        <div ref={searchDivRef}>
          <TextInput
            value={searchText}
            onChange={onSearchTextChanged}
            placeholder="Search To Add Users"
            data-testid={'add-user-search'}
            icon="search"
            width="100%"
            onFocus={open}
            onClick={open}
          />
        </div>
      </InlineDialog>
    </>
  );
};

export default AddUsersSearch;
